import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, User, Accessibility, HeartHandshake, Leaf } from "lucide-react";
import { useSeo } from "../components/site/Layout";
import { PageHero, Section, ThirdPartyNotice, AidNotice, CtaBand, Faq } from "../components/site/blocks";
import { Reveal } from "../components/site/Reveal";
import { IMAGES, SERVICES } from "../data/site";

const PROFILES = [
  { icon: User, t: "60 ans et plus", d: "Rester chez soi, en sécurité.", more: "Nous identifions les aménagements utiles (douche, accès, éclairage) et les aides auxquelles vous pouvez prétendre." },
  { icon: Accessibility, t: "Situation de handicap", d: "Un logement adapté à vos besoins.", more: "Solutions construites à partir de vos usages, avec l'avis de vos professionnels de santé si besoin." },
  { icon: HeartHandshake, t: "Aidant familial", d: "Un relais pour vos démarches.", more: "Un interlocuteur unique, des comptes rendus écrits, moins de charge mentale, même à distance." },
  { icon: Leaf, t: "Rénovation énergétique", d: "Confort et factures maîtrisées.", more: "Un projet cohérent et finançable, conforme aux règles MaPrimeRénov' 2026." },
];

export default function Particuliers() {
  useSeo("Particuliers : seniors, handicap, aidants, rénovation", "Accompagnement des particuliers pour adapter, sécuriser et rénover leur logement : diagnostic, aides MaPrimeAdapt' et MaPrimeRénov', coordination des travaux.");
  return (
    <>
      <PageHero eyebrow="Particuliers" title="Un logement qui suit votre vie" intro="Écoute, diagnostic, aides, travaux : nous vous guidons pas à pas."
        image={IMAGES.aidant} imageAlt="Une aidante et son père âgé souriant sur un canapé en consultant une tablette">
        <Link to="/prendre-rdv" className="btn-accent" data-testid="particuliers-rdv-cta">Prendre RDV diagnostic <ArrowRight className="h-5 w-5" aria-hidden="true" /></Link>
        <Link to="/simulateur" className="btn-outline" data-testid="particuliers-simulator-cta">Tester mon éligibilité</Link>
        <Link to="/contact?type=rappel" className="btn-outline" data-testid="particuliers-callback-cta">Être rappelé</Link>
      </PageHero>
      <Section eyebrow="Votre situation" title="Nous vous accompagnons si…">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PROFILES.map((p, i) => (
            <Reveal key={p.t} delay={i * 70}>
              <details className="group bento p-6 h-full open:bg-brand-50" data-testid={`particulier-profile-${i}`}>
                <summary className="list-none cursor-pointer">
                  <span className="h-11 w-11 rounded-2xl bg-brand-100 text-brand-ink flex items-center justify-center"><p.icon className="h-5 w-5" aria-hidden="true" /></span>
                  <h3 className="mt-6 text-xl">{p.t}</h3>
                  <p className="mt-1 text-slate-600 text-[15px]">{p.d}</p>
                </summary>
                <p className="mt-3 text-[15px] text-slate-700">{p.more}</p>
              </details>
            </Reveal>
          ))}
        </div>
      </Section>
      <Section tone="mist" eyebrow="Services" title="Les plus demandés">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SERVICES.filter((s) => ["adaptation-logement", "securisation", "montage-dossiers-aides", "renovation-energetique"].includes(s.slug)).map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} className="group bento p-6" data-testid={`particuliers-service-${s.slug}`}>
              <s.icon className="h-6 w-6 text-brand-ink" aria-hidden="true" />
              <h3 className="mt-6 text-lg">{s.title}</h3>
              <ArrowUpRight className="absolute right-5 top-5 h-5 w-5 text-brand-ink" aria-hidden="true" />
            </Link>
          ))}
        </div>
        <div className="mt-6 grid lg:grid-cols-2 gap-4"><ThirdPartyNotice /><AidNotice /></div>
      </Section>
      <Section eyebrow="Questions" title="En bref">
        <Faq prefix="particuliers-faq" items={[
          { q: "Je suis locataire, est-ce possible ?", a: "Dans le parc privé, oui avec l'accord écrit du propriétaire (MaPrimeAdapt' possible sous conditions). En logement social, l'adaptation relève du bailleur : nous vous aidons à formuler la demande." },
          { q: "Un proche peut-il gérer le dossier ?", a: "Oui, avec votre accord. Nous adaptons nos comptes rendus pour que chacun soit informé." },
          { q: "Combien coûte l'accompagnement ?", a: "Selon la mission : voir nos forfaits sur la page Tarifs, ou un devis écrit avant tout engagement." },
        ]} />
      </Section>
      <CtaBand />
    </>
  );
}
